/**
 * Admin Deliverables Store
 *
 * Manages all deliverables across all projects for admin users
 */
import { defineStore } from 'pinia';
import { ref, shallowRef, computed } from 'vue';
import { deliverableService } from '@/services/deliverableService';
import { db } from '@/js/firebase';
import {
  collection,
  onSnapshot,
  getDocs
} from 'firebase/firestore';
import { logger } from '@/services/loggerService';

export const useAdminDeliverablesStore = defineStore('adminDeliverables', () => {
  //-------------------------------
  // STATE
  //-------------------------------

  /**
   * All deliverables indexed by ID
   * shallowRef: large collection replaced wholesale via onSnapshot
   * @type {Object}
   */
  const items = shallowRef({});

  /**
   * Loading state
   * @type {Boolean}
   */
  const loading = ref(false);

  /**
   * Store initialization state
   * @type {Boolean}
   */
  const initialized = ref(false);

  /**
   * Error state
   * @type {String|null}
   */
  const error = ref(null);

  /**
   * Unsubscribe function for deliverables listener
   * @type {Function|null}
   */
  let deliverablesUnsubscribe = null;

  /**
   * Projects data cache for enriching deliverables
   * shallowRef: large collection replaced wholesale
   * @type {Object}
   */
  const projectsCache = shallowRef({});

  /**
   * Companies data cache for enriching deliverables
   * shallowRef: large collection replaced wholesale
   * @type {Object}
   */
  const companiesCache = shallowRef({});

  /**
   * Minimized deliverables list (persisted in localStorage)
   * @type {Array}
   */
  const minimizedDeliverables = ref([]);

  /**
   * Modal state for opening deliverables globally
   * @type {Object}
   */
  const modalState = ref({
    isOpen: false,
    deliverable: null,
    project: null,
    company: null
  });

  /**
   * Version counter to track modal opens and prevent stale close operations
   * from clearing data when a new modal has been opened
   */
  let modalVersion = 0;

  //-------------------------------
  // LOCALSTORAGE HELPERS
  //-------------------------------

  const MINIMIZED_STORAGE_KEY = 'admin-minimized-deliverables';

  /**
   * Load minimized deliverables from localStorage
   */
  const loadMinimizedFromStorage = () => {
    try {
      const stored = localStorage.getItem(MINIMIZED_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        logger.info('[AdminDeliverables] Loaded minimized deliverables from localStorage', {
          count: parsed.length
        });
        return parsed;
      }
    } catch (error) {
      logger.error('[AdminDeliverables] Failed to load minimized deliverables from localStorage', error);
    }
    return [];
  };

  /**
   * Save minimized deliverables to localStorage
   */
  const saveMinimizedToStorage = (deliverables) => {
    try {
      localStorage.setItem(MINIMIZED_STORAGE_KEY, JSON.stringify(deliverables));
      logger.info('[AdminDeliverables] Saved minimized deliverables to localStorage', {
        count: deliverables.length
      });
    } catch (error) {
      logger.error('[AdminDeliverables] Failed to save minimized deliverables to localStorage', error);
    }
  };

  //-------------------------------
  // GETTERS
  //-------------------------------

  /**
   * Get deliverable by ID
   */
  const getDeliverableById = computed(() => (id) => {
    return items.value[id] || null;
  });

  /**
   * Get all deliverables as array
   */
  const allDeliverables = computed(() => {
    return Object.values(items.value);
  });

  /**
   * Get deliverables grouped by stage
   */
  const deliverablesByStage = computed(() => {
    const grouped = {};

    Object.values(items.value).forEach(deliverable => {
      const stage = deliverable.stage || 'not_ready';
      if (!grouped[stage]) {
        grouped[stage] = [];
      }
      grouped[stage].push(deliverable);
    });

    return grouped;
  });

  /**
   * Get deliverables for a specific project
   */
  const getDeliverablesByProject = computed(() => (projectId) => {
    return Object.values(items.value).filter(d => d.projectId === projectId);
  });

  /**
   * Get deliverables for a specific company
   */
  const getDeliverablesByCompany = computed(() => (companyId) => {
    return Object.values(items.value).filter(d => d.companyId === companyId);
  });

  /**
   * Check if a deliverable is a clone
   */
  const isClonedDeliverable = computed(() => (id) => {
    const deliverable = items.value[id];
    return deliverable?.clonedFromDeliverableId ? true : false;
  });

  //-------------------------------
  // ACTIONS
  //-------------------------------

  /**
   * Load projects data for enrichment
   */
  async function loadProjectsData() {
    try {
      const projectsRef = collection(db, 'projects');
      const snapshot = await getDocs(projectsRef);

      const projects = {};
      snapshot.forEach(doc => {
        projects[doc.id] = {
          id: doc.id,
          ...doc.data()
        };
      });

      projectsCache.value = projects;
      logger.info('[AdminDeliverables] Loaded projects cache', {
        count: Object.keys(projects).length
      });
    } catch (error) {
      logger.error('[AdminDeliverables] Failed to load projects data:', error);
    }
  }

  /**
   * Load companies data for enrichment
   */
  async function loadCompaniesData() {
    try {
      const companiesRef = collection(db, 'companies');
      const snapshot = await getDocs(companiesRef);

      const companies = {};
      snapshot.forEach(doc => {
        companies[doc.id] = {
          id: doc.id,
          ...doc.data()
        };
      });

      companiesCache.value = companies;
      logger.info('[AdminDeliverables] Loaded companies cache', {
        count: Object.keys(companies).length
      });
    } catch (error) {
      logger.error('[AdminDeliverables] Failed to load companies data:', error);
    }
  }

  /**
   * Enrich deliverable with project and company data
   */
  function enrichDeliverable(deliverable) {
    const enriched = { ...deliverable };

    // Add project data
    if (deliverable.projectId && projectsCache.value[deliverable.projectId]) {
      const project = projectsCache.value[deliverable.projectId];
      enriched.projectName = project.name || 'Unknown Project';
      enriched.companyId = project.company || project.companyId;
      enriched.companyName = project.companyName;
    }

    // Add company data if not already present
    if (enriched.companyId && !enriched.companyName && companiesCache.value[enriched.companyId]) {
      enriched.companyName = companiesCache.value[enriched.companyId].name;
    }

    return enriched;
  }

  /**
   * Initialize the store and load all deliverables
   */
  async function initialize() {
    if (initialized.value) {
      logger.info('[AdminDeliverables] Store already initialized');
      return;
    }

    try {
      loading.value = true;
      error.value = null;

      // Load supporting data first
      await Promise.all([
        loadProjectsData(),
        loadCompaniesData()
      ]);

      // Set up real-time listener for all deliverables.
      // No orderBy: Firestore drops docs missing the ordered field, so
      // deliverables without a dueDate vanished from every admin count/board.
      const deliverablesRef = collection(db, 'deliverables');

      deliverablesUnsubscribe = onSnapshot(
        deliverablesRef,
        (snapshot) => {
          const sorted = snapshot.docs
            .map(doc => enrichDeliverable({ id: doc.id, ...doc.data() }))
            .sort((a, b) => (a.dueDate?.toMillis?.() ?? Number.MAX_SAFE_INTEGER) - (b.dueDate?.toMillis?.() ?? Number.MAX_SAFE_INTEGER));

          const deliverables = {};
          sorted.forEach(d => { deliverables[d.id] = d; });
          items.value = deliverables;

          logger.info('[AdminDeliverables] Deliverables updated', {
            count: sorted.length
          });

          if (!initialized.value) {
            initialized.value = true;
          }
          loading.value = false;
        },
        (err) => {
          logger.firestoreListenerError('[AdminDeliverables] Snapshot error:', err);
          error.value = err.message;
          loading.value = false;
        }
      );
    } catch (error) {
      logger.error('[AdminDeliverables] Failed to initialize:', error);
      error.value = error.message;
      loading.value = false;
    }
  }

  /**
   * Refresh project and company data
   */
  async function refreshEnrichmentData() {
    try {
      await Promise.all([
        loadProjectsData(),
        loadCompaniesData()
      ]);

      // Re-enrich all deliverables
      const enrichedItems = {};
      Object.entries(items.value).forEach(([id, deliverable]) => {
        enrichedItems[id] = enrichDeliverable(deliverable);
      });
      items.value = enrichedItems;

      logger.info('[AdminDeliverables] Enrichment data refreshed');
    } catch (error) {
      logger.error('[AdminDeliverables] Failed to refresh enrichment data:', error);
    }
  }

  /**
   * Create a new deliverable
   */
  async function createDeliverable(deliverableData) {
    try {
      const result = await deliverableService.createDeliverable(deliverableData);

      // Refresh enrichment data to ensure new deliverable has proper project/company info
      await refreshEnrichmentData();

      return result;
    } catch (error) {
      logger.error('[AdminDeliverables] Failed to create deliverable:', error);
      throw error;
    }
  }

  /**
   * Update a deliverable
   */
  async function updateDeliverable(deliverableId, updates, showToast = true) {
    try {
      return await deliverableService.updateDeliverable(deliverableId, updates, showToast);
    } catch (error) {
      logger[error.isValidation ? 'warn' : 'error']('[AdminDeliverables] Failed to update deliverable:', error);
      throw error;
    }
  }

  /**
   * Delete a deliverable
   */
  async function deleteDeliverable(deliverableId, options = {}) {
    try {
      return await deliverableService.deleteDeliverable(deliverableId, options);
    } catch (error) {
      logger.error('[AdminDeliverables] Failed to delete deliverable:', error);
      throw error;
    }
  }

  /**
   * Update deliverable stage
   */
  async function updateDeliverableStage(deliverableId, newStage) {
    try {
      return await deliverableService.updateDeliverableStage(deliverableId, newStage);
    } catch (error) {
      logger[error.isValidation ? 'warn' : 'error']('[AdminDeliverables] Failed to update stage:', error);
      throw error;
    }
  }

  /**
   * Clone and detach a deliverable
   */
  async function cloneAndDetachDeliverable(deliverableId) {
    try {
      const result = await deliverableService.cloneAndDetachDeliverable(deliverableId);

      // Refresh enrichment data to ensure new deliverable has proper company info
      await refreshEnrichmentData();

      return result;
    } catch (error) {
      logger.error('[AdminDeliverables] Failed to clone and detach deliverable:', error);
      throw error;
    }
  }

  /**
   * Get original project brief for a cloned deliverable
   */
  async function getOriginalProjectBrief(deliverableId) {
    try {
      return await deliverableService.getOriginalProjectBrief(deliverableId);
    } catch (error) {
      logger.error('[AdminDeliverables] Failed to get original project brief:', error);
      throw error;
    }
  }

  /**
   * Reset the store
   */
  function reset() {
    items.value = {};
    loading.value = false;
    initialized.value = false;
    error.value = null;
    projectsCache.value = {};
    companiesCache.value = {};
    // Don't clear minimizedDeliverables - reload from localStorage to persist across page navigation
    minimizedDeliverables.value = loadMinimizedFromStorage();
    modalState.value = {
      isOpen: false,
      deliverable: null,
      project: null,
      company: null
    };

    // Cleanup subscription
    if (deliverablesUnsubscribe) {
      deliverablesUnsubscribe();
      deliverablesUnsubscribe = null;
    }

    logger.info('[AdminDeliverables] Store reset completed');
  }

  //-------------------------------
  // MINIMIZED DELIVERABLES ACTIONS
  //-------------------------------

  /**
   * Minimize a deliverable
   */
  function minimizeDeliverable(deliverable) {
    // Check if already minimized
    const existingMinimized = minimizedDeliverables.value.find(d => d.id === deliverable.id);
    if (existingMinimized) {
      logger.warn('[AdminDeliverables] Deliverable already minimized', {
        deliverableId: deliverable.id,
        title: deliverable.title,
        currentMinimizedCount: minimizedDeliverables.value.length,
        minimizedList: minimizedDeliverables.value.map(d => ({ id: d.id, title: d.title }))
      });
      return;
    }

    // Add to minimized list with minimal data for localStorage
    minimizedDeliverables.value.push({
      id: deliverable.id,
      title: deliverable.title,
      type: deliverable.type,
      status: deliverable.status,
      stage: deliverable.stage,
      projectId: deliverable.project?.id || deliverable.projectId,
      companyId: deliverable.company?.id || deliverable.companyId,
      assignedUserId: deliverable.assignedUserId,
      dueDate: deliverable.dueDate ? (deliverable.dueDate.toDate ? deliverable.dueDate.toDate().toISOString() : deliverable.dueDate) : null
    });

    // Save to localStorage
    saveMinimizedToStorage(minimizedDeliverables.value);

    logger.info('[AdminDeliverables] Deliverable minimized', {
      deliverableId: deliverable.id,
      title: deliverable.title,
      totalMinimized: minimizedDeliverables.value.length
    });
  }

  /**
   * Restore a minimized deliverable
   */
  function restoreDeliverable(deliverableId) {
    const index = minimizedDeliverables.value.findIndex(d => d.id === deliverableId);

    if (index === -1) {
      logger.warn('[AdminDeliverables] Deliverable not found in minimized list', {
        deliverableId
      });
      return null;
    }

    const deliverable = minimizedDeliverables.value[index];
    minimizedDeliverables.value.splice(index, 1);

    // Save to localStorage
    saveMinimizedToStorage(minimizedDeliverables.value);

    logger.info('[AdminDeliverables] Deliverable restored', {
      deliverableId,
      title: deliverable.title,
      remainingMinimized: minimizedDeliverables.value.length
    });

    return deliverable;
  }

  /**
   * Check if a deliverable is minimized
   */
  function isMinimized(deliverableId) {
    return minimizedDeliverables.value.some(d => d.id === deliverableId);
  }

  /**
   * Clear all minimized deliverables
   */
  function clearMinimized() {
    minimizedDeliverables.value = [];
    saveMinimizedToStorage([]);
    logger.info('[AdminDeliverables] Cleared all minimized deliverables');
  }

  /**
   * Open deliverable modal and update URL for deep linking
   */
  function openDeliverableModal(deliverable, project, company) {
    // Prevent double-opening the same deliverable
    if (modalState.value.isOpen && modalState.value.deliverable?.id === deliverable?.id) {
      logger.debug('[AdminDeliverables] Modal already open for this deliverable, skipping', {
        deliverableId: deliverable?.id
      });
      return;
    }

    // Increment version to invalidate any pending close operations
    modalVersion++;
    logger.debug('[AdminDeliverables] Modal version incremented', { version: modalVersion });

    modalState.value = {
      isOpen: true,
      deliverable,
      project,
      company
    };

    // Update URL with deliverable ID for deep linking
    // Router is loaded lazily to avoid a circular dependency (store -> router -> views -> store)
    import('@/router').then(({ default: router }) => {
      const currentPath = router.currentRoute.value.path;
      logger.debug('[AdminDeliverables] Deep link check', {
        deliverableId: deliverable?.id,
        currentPath,
        startsWithNexus: currentPath.startsWith('/nexus/')
      });

      if (deliverable?.id && currentPath.startsWith('/nexus/')) {
        const currentQuery = { ...router.currentRoute.value.query };
        if (currentQuery.deliverable !== deliverable.id) {
          logger.debug('[AdminDeliverables] Updating URL with deliverable ID', {
            deliverableId: deliverable.id,
            currentQuery
          });
          router.replace({
            path: currentPath,
            query: { ...currentQuery, deliverable: deliverable.id }
          });
        }
      }
    }).catch((err) => {
      logger.error('[AdminDeliverables] Error updating URL for deep link', { error: err.message });
    });

    logger.info('[AdminDeliverables] Opening deliverable modal', {
      deliverableId: deliverable?.id,
      title: deliverable?.title
    });
  }

  /**
   * Close deliverable modal and remove URL query parameter
   */
  function closeDeliverableModal() {
    modalState.value.isOpen = false;

    // Remove deliverable query parameter from URL (router loaded lazily, see openDeliverableModal)
    import('@/router').then(({ default: router }) => {
      const currentQuery = router.currentRoute.value.query;
      if (currentQuery.deliverable) {
        const newQuery = { ...currentQuery };
        delete newQuery.deliverable;
        router.replace({ query: newQuery });
      }
    }).catch((err) => {
      logger.error('[AdminDeliverables] Error clearing deep link query param', { error: err.message });
    });

    // Capture the current version at close time
    const closeVersion = modalVersion;

    // Keep data for animation, but only clear if no new modal has been opened
    // This prevents a race condition when opening a new deliverable immediately after closing
    setTimeout(() => {
      // Only clear if the modal version hasn't changed (no new modal opened)
      // and the modal is still closed
      if (modalVersion === closeVersion && !modalState.value.isOpen) {
        modalState.value.deliverable = null;
        modalState.value.project = null;
        modalState.value.company = null;
        logger.debug('[AdminDeliverables] Cleared modal data', { version: closeVersion });
      } else {
        logger.debug('[AdminDeliverables] Skipped clearing modal data - new modal opened', {
          closeVersion,
          currentVersion: modalVersion,
          isOpen: modalState.value.isOpen
        });
      }
    }, 300);

    logger.info('[AdminDeliverables] Closing deliverable modal');
  }

  /**
   * Load minimized deliverables from localStorage on init
   */
  function loadMinimizedDeliverables() {
    minimizedDeliverables.value = loadMinimizedFromStorage();
  }

  // Load minimized deliverables immediately
  loadMinimizedDeliverables();

  return {
    // State
    items,
    loading,
    initialized,
    error,
    minimizedDeliverables,
    modalState,

    // Getters
    getDeliverableById,
    allDeliverables,
    deliverablesByStage,
    getDeliverablesByProject,
    getDeliverablesByCompany,
    isClonedDeliverable,

    // Actions
    initialize,
    refreshEnrichmentData,
    createDeliverable,
    updateDeliverable,
    deleteDeliverable,
    updateDeliverableStage,
    cloneAndDetachDeliverable,
    getOriginalProjectBrief,
    reset,

    // Minimized Deliverables Actions
    minimizeDeliverable,
    restoreDeliverable,
    isMinimized,
    clearMinimized,
    openDeliverableModal,
    closeDeliverableModal
  };
});